import React, { useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { IoCheckmark } from "react-icons/io5";
import { DEVICE_TYPES, MODELS, TRAITS, isModel, isType } from "../../../constants/devices";
import { UserService } from "../../../services/UserService";
import AppModal from "../../../components/layout/app/AppModal";
import Sheet from "../../../components/layout/app/Sheet";
import { SETTINGS_PATHS } from "./paths";
import { SettingsPage, SettingsSkeleton, Unavailable, useAlive } from "./shared";
import { NumberDialog, SwitchRow, ValueRow, isEnabled, useMeterSettings, valueOf } from "./meter";

const SETTINGS = Number(TRAITS.METER_SETTINGS);
const ALARMS = Number(TRAITS.METER_ALARMS);

// The rows with a number, in the app's order. key is the name the meter
// reports the setting under.
const RECLOSING_TIMES = {
  key: "max_auto_reclose_attempts",
  id: 2,
  trait: SETTINGS,
  title: "Reclosing allowed times",
  description: "Limits retry attempts if a fault keeps happening continuously.",
  min: 1,
  max: 30,
};
const POWER_ON_DELAY = {
  key: "power_up_delay_time",
  id: 3,
  trait: SETTINGS,
  title: "Power-on delay time",
  description: "Waits a few seconds before giving power to your home.",
  min: 1,
  max: 9999,
};
const SWITCH_DELAY = {
  key: "manual_switch_delay",
  id: 5,
  trait: SETTINGS,
  title: "Switch delay",
  description: "Delays turning the switch ON or OFF after you tap it.",
  min: 0,
  max: 9999,
};
// W3EMP_1 only. The app's Current Multiplier (trait 1403, id 6) isn't here:
// its alarm screen sends 1403 id 6 for the voltage event time, so one of the
// two is wrong, and the web leaves both out until that's settled.
const VOLTAGE_MULTIPLIER = {
  key: "voltage_multiplier",
  id: 5,
  trait: ALARMS,
  title: "Voltage Multiplier",
  description: "Fixes the meter if it shows the wrong voltage.",
  min: 0,
  max: 9999,
};

const RECLOSING = { key: "auto_reclose_enable", id: 1, trait: SETTINGS };
const RELAY = { key: "relay_power_on_status", id: 4, trait: SETTINGS };
const RELAY_OPTIONS = [
  { value: 0, label: "Off" },
  { value: 1, label: "On" },
  { value: 2, label: "Memory / Last State" },
];
const RELAY_LABELS = ["Off", "On", "Memory"];

// The meter's alarm counters in its latest telemetry.
const COUNTS = [
  { key: "over_current_cnt", label: "Over current count" },
  { key: "lost_current_cnt", label: "Lost current count" },
  { key: "leak_cnt", label: "Leakage count" },
];

// Settings for the W1EMP_1 / W3EMP_1 energy meters (app/screens/DeviceSettings/
// EnergyMeterSettings.js): the way to the alarm limits, the alarm counts, and
// the reclosing, delay and relay settings.
function EnergyMeterSettings() {
  const { state } = useLocation();
  const history = useHistory();
  const alive = useAlive();
  const device = state?.data;
  const supported = isType(device, DEVICE_TYPES.ENERGY_METER) && Boolean(device?.deviceId);
  const { settings, status, busy, send, retry } = useMeterSettings(supported ? device : null);
  const [editor, setEditor] = useState(null);
  const [relayOpen, setRelayOpen] = useState(false);
  // The Alarm Times dialog: { counts } or { error: true } while it's open.
  const [alarmTimes, setAlarmTimes] = useState(null);
  const [reading, setReading] = useState(false);

  if (!device) return <Redirect to="/dashboard" />;

  const relay = Number(valueOf(settings, RELAY.key, "2"));
  const reclosing = isEnabled(settings, RECLOSING.key);
  const multipliers = isModel(device, MODELS.W3EMP_1) ? [VOLTAGE_MULTIPLIER] : [];

  const edit = (row) => {
    if (!busy) setEditor({ ...row, value: valueOf(settings, row.key) });
  };

  const saveNumber = (value) => {
    const row = editor;
    setEditor(null);
    send({ id: row.id, trait: row.trait, value }, row.key);
  };

  const toggleReclosing = () => {
    if (!busy) send({ id: RECLOSING.id, trait: RECLOSING.trait, value: Number(!reclosing) }, RECLOSING.key);
  };

  // As in the app, choosing a status sends it straight away.
  const pickRelay = (value) => {
    setRelayOpen(false);
    if (value !== relay) send({ id: RELAY.id, trait: RELAY.trait, value }, RELAY.key);
  };

  const openAlarmTimes = async () => {
    if (busy || reading) return;
    setReading(true);
    let result;
    try {
      result = { counts: (await UserService.getEnergyTelemetry(device.deviceId)) ?? {} };
    } catch (error) {
      result = { error: true };
    }
    if (!alive.current) return;
    setReading(false);
    setAlarmTimes(result);
  };

  const numberRow = (row) => (
    <ValueRow key={row.key} title={row.title} description={row.description} value={valueOf(settings, row.key)} onOpen={() => edit(row)} />
  );

  let body;
  if (!supported) {
    body = <Unavailable />;
  } else if (status === "loading") {
    body = <SettingsSkeleton tall />;
  } else if (status === "error") {
    body = (
      <div className="hx-empty hx-set-empty" role="alert">
        Couldn't read this device's settings.
        <button type="button" className="hx-btn" onClick={retry}>
          Try again
        </button>
      </div>
    );
  } else {
    // The app's order.
    body = (
      <ul className="hx-set-em-list">
        <ValueRow title="Alarm settings" opensPage onOpen={() => history.push(SETTINGS_PATHS.METER_ALARMS, { data: device })} />
        <ValueRow title="Alarm times" onOpen={openAlarmTimes} />
        {numberRow(RECLOSING_TIMES)}
        <SwitchRow
          id="hx-set-em-reclosing"
          title="Reclosing enable"
          description="Automatically turns power back on once conditions become safe."
          checked={reclosing}
          onToggle={toggleReclosing}
        />
        {numberRow(POWER_ON_DELAY)}
        <ValueRow
          title="Relay power-on status setting"
          description="Set state after power returns: Off, On, or Last state."
          value={RELAY_LABELS[relay] ?? String(relay)}
          onOpen={() => {
            if (!busy) setRelayOpen(true);
          }}
        />
        {numberRow(SWITCH_DELAY)}
        {multipliers.map(numberRow)}
      </ul>
    );
  }

  return (
    <SettingsPage title="Settings" device={device} busy={busy || (reading ? "Please wait…" : null)}>
      {body}

      {editor ? <NumberDialog editor={editor} onClose={() => setEditor(null)} onSave={saveNumber} /> : null}

      <Sheet open={relayOpen} onClose={() => setRelayOpen(false)} title="Relay power-on status setting">
        <div className="hx-set-em-options">
          {RELAY_OPTIONS.map((option) => {
            const picked = option.value === relay;
            return (
              <button
                key={option.value}
                type="button"
                className={`hx-set-em-option ${picked ? "is-picked" : ""}`}
                aria-pressed={picked}
                onClick={() => pickRelay(option.value)}
              >
                {option.label}
                {picked ? <IoCheckmark aria-hidden="true" /> : null}
              </button>
            );
          })}
        </div>
      </Sheet>

      <AppModal
        open={Boolean(alarmTimes)}
        onClose={() => setAlarmTimes(null)}
        title="Alarm Times"
        actions={
          <button type="button" className="hx-btn" onClick={() => setAlarmTimes(null)}>
            Close
          </button>
        }
      >
        {alarmTimes?.error ? (
          <p className="hx-modal-text">Couldn't read the alarm counts. Please try again.</p>
        ) : (
          <dl className="hx-set-em-counts">
            {COUNTS.map((count) => (
              <div key={count.key}>
                <dt>{count.label}</dt>
                <dd>{valueOf(alarmTimes?.counts, count.key)}</dd>
              </div>
            ))}
          </dl>
        )}
      </AppModal>
    </SettingsPage>
  );
}

export default EnergyMeterSettings;
